import { inject, injectable } from 'inversify'
import { setup } from 'xstate'
import { TYPES } from '@/plugins/cad/module/container/types'
import type { KeyEventParams, MouseEventParams } from '@/plugins/cad/module/core/common/event'
import { StateMachineTool } from '@/plugins/cad/module/core/tool/StateMachineTool'
import { CadTipKey } from '@/plugins/cad/module/i18n/tipKeys'
import { KeyCodes } from '@/shared/constants'
import type { AuxiliaryMouseHelper } from '../auxiliaryTool/auxiliaryMouseHelper'
import { type MouseTipHelper, MouseTipId } from '../auxiliaryTool/MouseTipHelper'
import type { CADController } from '../CADController'
import { DrawHatchCommand } from '../command/DrawHatchCommand'
import type { CADViewManager } from '../manager/CADViewManager'
import type { HistoryManager } from '../manager/HistoryManager'
import type { IPoint } from '../model/common/interface'
import type { HatchEntity } from '../model/entity'
import type { EntitySelector } from '../selector/entitySelector'

const hatchMachine = setup({
  actions: {
    showMouseTip: (..._args: any) => {},
  },
}).createMachine({
  id: 'drawHatchTool',
  initial: 'idle',
  states: {
    idle: {
      on: {
        NEXT: [{ target: 'draw.point1' }],
      },
    },
    draw: {
      initial: 'point1',
      states: {
        point1: { entry: { type: 'showMouseTip', params: CadTipKey.draw.hatch.target } },
      },
    },
  },
})

/**
 * 填充绘制工具，在拾取的封闭区域中创建填充实体。
 */
@injectable()
export class DrawHatchTool extends StateMachineTool<typeof hatchMachine> {
  /** 工具名，等同于容器注册的符号 */
  name = TYPES.DrawHatchTool

  @inject(TYPES.CADViewManager)
  private readonly viewManager!: CADViewManager

  @inject(TYPES.EntitySelector)
  private readonly entitySelector!: EntitySelector

  @inject(TYPES.MouseTipHelper)
  private readonly mouseTipHelper!: MouseTipHelper

  @inject(TYPES.HistoryManager)
  private readonly historyManager!: HistoryManager

  @inject(TYPES.AuxiliaryMouseHelper)
  private readonly auxiliaryMouseHelper!: AuxiliaryMouseHelper

  protected machine = hatchMachine.provide({
    actions: {
      showMouseTip: (_v, tip) => {
        this.showMouseTip(tip)
      },
    },
  })

  /** 所属控制器，由框架自动注入 */
  declare controller: CADController
  private tempPoint: IPoint | null = null
  private currentCommand: DrawHatchCommand | null = null

  protected onInitialize(_entityData?: Partial<HatchEntity>): void {
    this.entitySelector.clearSelection()
    this.inject(this.auxiliaryMouseHelper)

    this.currentCommand = new DrawHatchCommand(this.viewManager)
    this.historyManager.startCommand(this.currentCommand)
    this.send({ type: 'NEXT' })
  }

  protected onTerminate(): void {
    this.historyManager.commitCommand()
    this.mouseTipHelper.removeTip(MouseTipId.Draw)
    this.eject(this.auxiliaryMouseHelper)

    this.tempPoint = null
    this.currentCommand = null
  }

  /**
   * 鼠标按下：左键尝试在当前点创建填充实体。
   *
   * @param params - 鼠标事件参数
   */
  /************ 鼠标键盘事件处理 ************/
  onMouseDown(params: MouseEventParams) {
    if (params.event.button === 0) {
      // 左键
      if (this.tempPoint) {
        // TODO: 创建实体并添加到命令中
        this.historyManager.refresh()
      }
    } else if (params.event.button === 2) {
      // 右键
    }
  }

  /**
   * 鼠标移动：更新填充待定点并刷新预览。
   *
   * @param params - 鼠标事件参数
   */
  onMouseMove(params: MouseEventParams) {
    this.tempPoint = { x: params.worldPos.x, y: params.worldPos.y }
    this.mouseCapture(params)
    // TODO: 实时预览填充效果
    this.updateCanvas()
  }

  /**
   * 键盘按下：ESC 退出工具。
   *
   * @param params - 键盘事件参数
   */
  onKeyDown(params: KeyEventParams) {
    if (params.event.keyCode === KeyCodes.ESC) {
      this.exit()
    }
  }

  // 正交捕捉处理
  private mouseCapture(params: MouseEventParams) {
    if (!this.tempPoint) {
      return
    }
    const finalPoint = this.auxiliaryMouseHelper.mouseCapture(params)
    this.tempPoint = { x: finalPoint.x, y: finalPoint.y }
  }

  /************ 状态机状态处理 ************/
  private showMouseTip(tip: string) {
    this.mouseTipHelper.addTip(MouseTipId.Draw, tip)
  }

  /************ 渲染 ************/
  private updateCanvas() {
    this.updateObject()
  }

  private updateObject() {}
}
